import type { CircleMarker, Map as LeafletMap } from "leaflet";
import L from "leaflet";
import { DRAW_PATH_OPTIONS, FENCE_COLORS } from "../constants";
import type { PolygonPreviewContext, PolygonPreviewRefs } from "../types/fence";
import { closePolygon } from "./fence.utils";

export function clearPolygonPreviewLayers(
	map: LeafletMap,
	refs: PolygonPreviewRefs
): void {
	if (refs.tempPolygonRef.current) {
		map.removeLayer(refs.tempPolygonRef.current);
	}
	if (refs.tempLineRef.current) {
		map.removeLayer(refs.tempLineRef.current);
	}
	for (const m of refs.tempMarkerRefs.current) {
		map.removeLayer(m);
	}
	refs.tempMarkerRefs.current = [];
}

function wireClosePolygonClick(
	ctx: PolygonPreviewContext,
	marker: CircleMarker
): void {
	marker.on("click", () => {
		if (ctx.drawingMode !== "polygon" || ctx.points.length < 3) {
			return;
		}
		const closedPoints = closePolygon(ctx.points);
		ctx.refs.tempPolygonRef.current = L.polygon(
			closedPoints.map((pt) => L.latLng(pt[0], pt[1])),
			DRAW_PATH_OPTIONS
		).addTo(ctx.map);

		ctx.clearTempPolygon();
		for (const closedPoint of closedPoints) {
			ctx.addPolygonPoint(closedPoint);
		}

		if (ctx.refs.tempLineRef.current) {
			ctx.map.removeLayer(ctx.refs.tempLineRef.current);
			ctx.refs.tempLineRef.current = null;
		}
		for (const m of ctx.refs.tempMarkerRefs.current) {
			ctx.map.removeLayer(m);
		}
		ctx.refs.tempMarkerRefs.current = [];
	});
	marker.bindTooltip("Clique para fechar", { direction: "top" });
}

function renderPolygonVertexMarkers(ctx: PolygonPreviewContext): void {
	for (let i = 0; i < ctx.points.length; i++) {
		const p = ctx.points[i];
		const isFirst = i === 0;
		const marker = L.circleMarker([p[0], p[1]], {
			radius: isFirst ? 8 : 5,
			color: FENCE_COLORS.PRIMARY,
			fillColor: isFirst ? FENCE_COLORS.PRIMARY : FENCE_COLORS.MARKER_FILL,
			fillOpacity: 1,
			weight: 2,
		}).addTo(ctx.map);

		if (isFirst && ctx.points.length >= 2) {
			wireClosePolygonClick(ctx, marker);
		}

		ctx.refs.tempMarkerRefs.current.push(marker);
	}
}

export function updatePolygonPreviewOnMap(ctx: PolygonPreviewContext): void {
	clearPolygonPreviewLayers(ctx.map, ctx.refs);

	if (ctx.points.length === 0) {
		return;
	}

	const latlngs = ctx.points.map((pt) => L.latLng(pt[0], pt[1]));

	if (ctx.points.length >= 2) {
		ctx.refs.tempLineRef.current = L.polyline(latlngs, {
			color: FENCE_COLORS.PRIMARY,
			weight: 2,
			dashArray: "5, 10",
		}).addTo(ctx.map);
	}

	renderPolygonVertexMarkers(ctx);
}
